// Color Replacement tool — replaces colors while preserving texture/shading.
// Works by shifting the hue/saturation of pixels toward the target color
// while preserving the original luminance and texture detail.

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import { mergeBBox } from "../pixel/brush";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import type { BBox } from "../pixel/brush";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export interface ColorReplacementSettings {
  size: number;
  hardness: number;
  opacity: number;
  flow: number;
  mode: "continuous" | "once" | "background-swatch";
  limits: "discontiguous" | "contiguous";
  tolerance: number;
}

export class ColorReplacementTool implements Tool {
  readonly id = "color-replacement";
  readonly shortcut = "B";

  settings: ColorReplacementSettings = {
    size: 30,
    hardness: 0.5,
    opacity: 1,
    flow: 1,
    mode: "continuous",
    limits: "contiguous",
    tolerance: 32,
  };

  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    lastX: number;
    lastY: number;
    bbox: BBox | null;
    sourceColor: { r: number; g: number; b: number } | null;
  } | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x;
    const docY = p.y - layer.y;

    // Sample source color. "Background Swatch" mode targets the current
    // background color directly rather than sampling a pixel under the
    // cursor — matches Photoshop's Color Replacement Sampling: Background
    // Swatch option (audit finding 181).
    const sourceColor = this.settings.mode === "background-swatch"
      ? { r: ctx.bgColor.r, g: ctx.bgColor.g, b: ctx.bgColor.b }
      : this.sampleColor(layer, docX, docY);

    this.active = {
      layer,
      layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
      lastX: docX,
      lastY: docY,
      bbox: null,
      sourceColor,
    };

    this.replace(ctx, docX, docY, ctx.fgColor);
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - this.active.layer.x;
    const docY = p.y - this.active.layer.y;

    // Re-sample in continuous mode
    if (this.settings.mode === "continuous") {
      this.active.sourceColor = this.sampleColor(this.active.layer, docX, docY);
    }

    const step = 2;
    const dx = docX - this.active.lastX;
    const dy = docY - this.active.lastY;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / step));

    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const cx = this.active.lastX + dx * t;
      const cy = this.active.lastY + dy * t;
      this.replace(ctx, cx, cy, ctx.fgColor);
    }

    this.active.lastX = docX;
    this.active.lastY = docY;
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;
    if (!this.active.bbox) { this.active = null; return; }

    const { layer, layerSnapshot, bbox } = this.active;
    const after = snapshotRegion(layer, bbox);
    const before = extractRegion(layerSnapshot, layer.pixels.width, bbox);
    this.active = null;
    ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
  }

  private sampleColor(layer: RasterLayer, docX: number, docY: number): { r: number; g: number; b: number } | null {
    const px = Math.floor(docX);
    const py = Math.floor(docY);
    const w = layer.pixels.width;
    if (px < 0 || py < 0 || px >= w || py >= layer.pixels.height) return null;

    // Sample 3x3 area
    let r = 0, g = 0, b = 0, count = 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const sx = px + dx;
        const sy = py + dy;
        if (sx < 0 || sy < 0 || sx >= w || sy >= layer.pixels.height) continue;
        const idx = (sy * w + sx) * 4;
        if (layer.pixels.data[idx + 3]! > 0) {
          r += layer.pixels.data[idx]!;
          g += layer.pixels.data[idx + 1]!;
          b += layer.pixels.data[idx + 2]!;
          count++;
        }
      }
    }
    if (count === 0) return null;
    return { r: Math.round(r / count), g: Math.round(g / count), b: Math.round(b / count) };
  }

  private replace(ctx: ToolContext, docX: number, docY: number, targetColor: { r: number; g: number; b: number }): void {
    if (!this.active || !this.active.sourceColor) return;

    const { layer, sourceColor } = this.active;
    const data = layer.pixels.data;
    const w = layer.pixels.width;
    const h = layer.pixels.height;
    const radius = this.settings.size / 2;
    const r2 = radius * radius;
    const tolerance = this.settings.tolerance;
    const opacity = this.settings.flow;

    const minX = Math.max(0, Math.floor(docX - radius));
    const minY = Math.max(0, Math.floor(docY - radius));
    const maxX = Math.min(w - 1, Math.ceil(docX + radius));
    const maxY = Math.min(h - 1, Math.ceil(docY + radius));

    // Convert target to HSL for luminance-preserving replacement
    const targetHSL = rgbToHsl(targetColor.r, targetColor.g, targetColor.b);

    let touchX0 = w, touchY0 = h, touchX1 = -1, touchY1 = -1;

    for (let py = minY; py <= maxY; py++) {
      const dy = py + 0.5 - docY;
      const dy2 = dy * dy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px + 0.5 - docX;
        if (dx * dx + dy2 >= r2) continue;

        const idx = (py * w + px) * 4;
        const alpha = data[idx + 3]!;
        if (alpha === 0) continue;

        const r = data[idx]!, g = data[idx + 1]!, b = data[idx + 2]!;

        // Check if pixel color is within tolerance of source
        const dr = r - sourceColor.r;
        const dg = g - sourceColor.g;
        const db = b - sourceColor.b;
        const dist = Math.sqrt(dr * dr + dg * dg + db * db);
        if (dist > tolerance) continue;

        // Soft-edge falloff
        const edgeDist = Math.sqrt(dx * dx + dy2) / radius;
        const falloff = edgeDist >= 1 ? 0 : edgeDist > this.settings.hardness
          ? (1 - edgeDist) / (1 - this.settings.hardness || 1e-6)
          : 1;

        const blendAmount = falloff * opacity;
        if (blendAmount <= 0) continue;

        // Preserve luminance: convert pixel to HSL, replace hue/sat with target, keep luminance
        const pixelHSL = rgbToHsl(r, g, b);
        // Blend between original and target hue/sat, keeping original luminance
        const newH = pixelHSL.h + (targetHSL.h - pixelHSL.h) * blendAmount;
        const newS = pixelHSL.s + (targetHSL.s - pixelHSL.s) * blendAmount;
        // Keep original luminance (this preserves texture/shading)
        const newL = pixelHSL.l;

        const rgb = hslToRgb(newH, newS, newL);
        data[idx] = Math.max(0, Math.min(255, Math.round(rgb.r)));
        data[idx + 1] = Math.max(0, Math.min(255, Math.round(rgb.g)));
        data[idx + 2] = Math.max(0, Math.min(255, Math.round(rgb.b)));

        if (px < touchX0) touchX0 = px;
        if (py < touchY0) touchY0 = py;
        if (px > touchX1) touchX1 = px;
        if (py > touchY1) touchY1 = py;
      }
    }

    if (touchX1 >= 0) {
      const bb: BBox = { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 };
      this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;
    }
    layer.pixels.markDirty();
  }
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}
